import { useEffect, useMemo } from 'react'
import { useFFEStore } from '../../../store/ffeStore'
import { SupplierCard } from './SupplierCard'
import { Button, Card, Typography } from 'antd'
import { SupplierCosts } from '../../../types/ffe'
import { useShallow } from 'zustand/react/shallow'
import { ArrowRight, Calculator, DollarSign, Truck } from 'lucide-react'
const { Text, Title } = Typography

export type SupplierCostingOverview = {
  costs: SupplierCosts
  name: string
  items: Array<{
    code: string
    name: string
    sku: string
    type: 'item' | 'material'
  }>
  id: string
}

export const DeliveryCostsPanel = () => {
  const lineItems = useFFEStore((state) => state.document.lineItems)
  const materials = useFFEStore((state) => state.document.materials)
  const supplierCosts = useFFEStore((state) => state.document.supplierCosts)
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const deliveryLineItem = useFFEStore(
    useShallow((state) =>
      state.document.lineItems.find(
        (item) => item.systemCategory === 'DELIVERY'
      )
    )
  )
  const defaultMargin = useFFEStore(
    (state) => state.document.settings.defaultMargin
  )
  const addLineItem = useFFEStore((state) => state.addLineItem)
  const suppliers = useFFEStore((state) => state.suppliers)

  const findSupplierById = (id: string) => {
    return suppliers.find((supplier) => supplier._id === id)
  }

  // Get unique suppliers and their items
  const itemsBySuppliers = useMemo<Array<SupplierCostingOverview>>(() => {
    const supplierMap = new Map<
      string,
      {
        name: string
        items: Array<{
          code: string
          sku: string
          name: string
          type: 'item' | 'material'
        }>
      }
    >()

    // Add line items
    lineItems.forEach((item) => {
      if (item.supplier) {
        const existing = supplierMap.get(item.supplier) || {
          name: findSupplierById(item.supplier)?.name || item.supplier,
          items: []
        }
        existing.items.push({
          code: item.code,
          sku: item.supplierItemCode || '',
          name: item.name,
          type: 'item'
        })
        supplierMap.set(item.supplier, existing)
      }
    })

    // Add materials
    materials.forEach((material) => {
      if (material.supplier) {
        const existing = supplierMap.get(material.supplier) || {
          name: findSupplierById(material.supplier)?.name || material.supplier,
          items: []
        }
        existing.items.push({
          code: '',
          name: material.name,
          type: 'material',
          sku: material.supplierItemCode || ''
        })
        supplierMap.set(material.supplier, existing)
      }
    })

    return Array.from(supplierMap.entries()).map(([id, data]) => ({
      id,
      ...data,
      costs: supplierCosts.find((cost) => cost.supplierId === id) || {
        id: crypto.randomUUID(),
        supplierId: id,
        deliveryCost: 0,
        margin: defaultMargin
      }
    }))
  }, [lineItems, materials, supplierCosts])

  const totalCosts = useMemo(() => {
    // return total cost price and sell price seperately
    const totalCostPrice = supplierCosts.reduce((total, supplier) => {
      return total + (supplier.deliveryCost ?? 0)
    }, 0)
    const totalSellPrice = supplierCosts.reduce((total, supplier) => {
      return (
        total +
        (supplier.deliveryCost ?? 0) *
          (1 + (supplier.margin ?? defaultMargin) / 100)
      )
    }, 0)

    return {
      totalCostPrice,
      totalSellPrice
    }
  }, [supplierCosts, defaultMargin])

  useEffect(() => {
    if (deliveryLineItem) {
      if (
        deliveryLineItem.costPrice === totalCosts.totalCostPrice &&
        deliveryLineItem.sellPrice === totalCosts.totalSellPrice
      ) {
        return
      }
      updateLineItem(deliveryLineItem.id, {
        costPrice: totalCosts.totalCostPrice,
        margin: (totalCosts.totalSellPrice - totalCosts.totalCostPrice) / totalCosts.totalCostPrice * 100,
        sellPrice: totalCosts.totalSellPrice
      })
    }
  }, [totalCosts])

  const createItem = () => {
    addLineItem(
      {
        systemCategory: 'DELIVERY',
        name: 'Delivery & Installation',
        description: 'Combined delivery & installation costs.',
        internalNotes:
          'Combined delivery and installation costs of all suppliers. Automatically generated line item from delivery costs.',
        quantity: 1,
        margin: (totalCosts.totalSellPrice - totalCosts.totalCostPrice) / totalCosts.totalCostPrice * 100,
        status: 'specified',
        costPrice: totalCosts.totalCostPrice,
        sellPrice: totalCosts.totalSellPrice
      },
      undefined,
      ''
    )
  }

  if (!deliveryLineItem) {
    return (
      <div className="space-y-8">
        {/* Hero section */}
        <div className="relative h-48 rounded-lg overflow-hidden mb-8">
          <img
            src="/delivery.png"
            alt="Delivery truck"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
          <div className="absolute inset-0 flex items-center p-8">
            <div className="text-white max-w-lg">
              <Title level={3} className="!text-white !mb-2">
                Delivery Cost Calculator
              </Title>
              <Text className="text-white/90 text-lg">
                Automatically calculate and track delivery, installation, and
                cleanup costs for your project
              </Text>
            </div>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <Card className="text-center">
            <Truck className="h-8 w-8 text-primary mx-auto mb-4" />
            <Text strong className="block mb-2">
              Per-Supplier Costs
            </Text>
            <Text type="secondary">
              Track delivery costs for each supplier separately
            </Text>
          </Card>

          <Card className="text-center">
            <Calculator className="h-8 w-8 text-primary mx-auto mb-4" />
            <Text strong className="block mb-2">
              Automatic Calculations
            </Text>
            <Text type="secondary">
              Costs are automatically summed and added to your document
            </Text>
          </Card>

          <Card className="text-center">
            <DollarSign className="h-8 w-8 text-primary mx-auto mb-4" />
            <Text strong className="block mb-2">
              Margin Control
            </Text>
            <Text type="secondary">
              Set margins and control pricing for delivery services
            </Text>
          </Card>
        </div>

        {/* Action button */}
        <div className="flex justify-center">
          <Button type="primary" size="large" onClick={createItem}>
            Add to Document
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="space-y-6 pb-4 px-2 flex grow flex-col overflow-y-auto">
        {itemsBySuppliers.map((items) => (
          <SupplierCard key={items.id} supplierCosting={items} />
        ))}

        {suppliers.length === 0 && (
          <Text type="secondary">
            No suppliers found. Add suppliers to line items or materials first.
          </Text>
        )}
      </div>

      {suppliers.length > 0 && (
        <div className="bottom-0 left-0 right-0 bg-white border-t p-4">
          <div className="flex items-center justify-between mb-4 ">
            <Text strong>Total Cost Price</Text>
            <Text strong>
              {totalCosts.totalCostPrice.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              })}
            </Text>
          </div>
          <div className="flex items-center justify-between mb-4 ">
            <Text strong>Total Sell Price</Text>
            <Text strong>
              {totalCosts.totalSellPrice.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              })}
            </Text>
          </div>
        </div>
      )}
    </div>
  )
}
